<template>
  <section class="exchange-form public-form">
    <div
      class="exchange-form__flow"
    >
      <ExchangeFlowSelect
        :value="flow"
        :isUkUser="props.isUkUser"
        @input="flowChangeHandler"
      />
    </div>
    <div class="exchange-form__header">
      <h1 id="form-heading" class="exchange-header__title" :style="headingStyles">{{ state.headLabel }}</h1>
    </div>
    <div class="exchange-form__body form-container">
      <ExchangeFormLayout :showError="Boolean(from.state.error) || Boolean(to.state.error)">
        <template v-slot:from-label>
          <label for="exchange-form-from">{{ $t('exchange-form.spend-label') }}</label>
        </template>
        <template v-slot:from-payment-icons>
          <PaymentSystemIcons v-if="fromFiatFlow" />
        </template>
        <template v-slot:from>
          <PbPopup v-model="from.state.showDropDown">
            <template #action="{toggle}">
              <ExchangeFormItem :error="Boolean(from.state.error)">
                <ExchangeLoading v-if="fromLoading" />
                <ExchangeInput
                  v-else
                  id="exchange-form-from"
                  v-model="from.state.amount"
                  :digits="fromFiatFlow ? 2 : 8"
                  @input="fromAmountHandler"
                />
                <ExchangeMoneyServiceLoading v-if="paymentMethodsState.loading" />
                <ExchangeMoneyService
                  v-else
                  :currency="fromSelectedMethod.currency"
                  :methodName="fromSelectedMethod.name"
                  @click="toggle"
                />
              </ExchangeFormItem>
            </template>
            <template #default="{hide}">
              <ExchangeDropDownFiat
                v-if="fromFiatFlow"
                v-model="fromSelected"
                :flow="flow"
                :items="fromDropDownList"
                :currencyList="fromFiatCurrenciesState.items"
                :loading="fromFiatMethodsLoading"
                :currency.sync="fromDropDownState.currency"
                @close="hide"
              />
              <template v-else-if="fromCryptoFlow">
                <ExchangeDropDownCryptoWallets
                  v-model="fromSelected"
                  :flow="flow"
                  :items="fromCryptoCurrenciesList"
                  :wallets="fromWebWalletOptions"
                  :allowWebWallets="allowWebWallets"
                  :loading="fromCryptoCurrenciesState.loading || webWalletsOptionsLoading"
                  @close="hide"
                />
              </template>
            </template>
          </PbPopup>
        </template>
        <template v-slot:from-error>
          <ExchangeFormItemError v-if="from.state.error">
            <div v-if="minMaxAmountError">
              {{minMaxAmountError.prefix}}
              <span class="exchange-form__error-action" @click="handleMinMaxAmountClick">{{minMaxAmountError.amount}}</span>
              {{minMaxAmountError.suffix}}
            </div>
            <span v-else>{{ from.state.error }}</span>
          </ExchangeFormItemError>
        </template>
        <template v-slot:from-description>
          <ExchangeInputEquivalent
            v-if="flow === Flow.CRYPTO_TO_FIAT"
            v-model="from.state.equivalentAmount"
            :currency="to.state.selected.currency"
            :loading="fromLoading"
            @input="fromAmountEquivalentHandler"
          />
          <div v-else-if="props.isPromoCodeAvailable && fromFiatFlow" class="exchange-form__promocode-block">
            <ExchangeFormPromoComponent
              v-model="promoCodeState.data.code"
              :capture="promoCodeState.data.capture"
              :error="promoCodeState.data.error"
            />
            <ExchangeFormPromoHint
              :hint="promoCodeState.data.hint"
            />
          </div>
          <ExchangeFormSelectedWallet
            v-if="from.state.selected instanceof WalletOptionModel"
            :wallet="from.state.selected"
            @clickCryptoBalance="onClickCryptoBalance"
          />
        </template>
        <template v-slot:to-label>
          <label for="exchange-from-to">{{ $t('exchange-form.receive-label') }}</label>
        </template>
        <template v-slot:to>
          <PbPopup v-model="to.state.showDropDown">
            <template #action="{toggle}">
              <ExchangeFormItem :error="Boolean(to.state.error)">
                <ExchangeLoading v-if="toLoading" />
                <ExchangeInput
                  v-else
                  id="exchange-from-to"
                  v-model="to.state.amount"
                  :digits="toCryptoFlow ? 8 : 2"
                  @input="toAmountHandler"
                />
                <ExchangeMoneyServiceLoading v-if="payoutMethodsState.loading" />
                <ExchangeMoneyService
                  v-else
                  :currency="toSelectedMethod.currency"
                  :methodName="toSelectedMethod.name"
                  @click="toggle"
                />
              </ExchangeFormItem>
            </template>
            <template #default="{hide}">
              <ExchangeDropDownFiat
                v-if="toFiatFlow"
                v-model="toSelected"
                :flow="flow"
                :items="toDropDownList"
                :currencyList="toFiatCurrenciesState.items"
                :loading="toFiatMethodsLoading"
                :currency.sync="toDropDownState.currency"
                @close="hide"
              />
              <template v-else-if="toCryptoFlow">
                <ExchangeDropDownCrypto
                  v-model="toSelected"
                  :flow="flow"
                  :items="toCryptoCurrenciesList"
                  :loading="toCryptoCurrenciesState.loading"
                  @close="hide"
                />
              </template>
            </template>
          </PbPopup>
        </template>
        <template v-slot:to-error>
          <ExchangeFormItemError v-if="to.state.error">{{ to.state.error }}</ExchangeFormItemError>
        </template>
        <template v-slot:to-description>
          <div v-if="treatAsGrossAmount" class="exchange-form__gross-amount-recalculation-promp">
            {{ $t('exchange-form.gross-amount-recalculation-prompt') }}
          </div>
          <ExchangeInputEquivalent
            v-if="flow === Flow.FIAT_TO_CRYPTO"
            v-model="to.state.equivalentAmount"
            :currency="from.state.selected.currency"
            :loading="toLoading"
            @input="toAmountEquivalentHandler"
          />
          <div v-else-if="fromCryptoFlow && props.isPromoCodeAvailable" class="exchange-form__promocode-block">
            <ExchangeFormPromoComponent
              v-model="promoCodeState.data.code"
              :capture="promoCodeState.data.capture"
              :error="promoCodeState.data.error"
            />
            <ExchangeFormPromoHint :hint="promoCodeState.data.hint" />
          </div>
<!--          <ExchangeFormSelectedWallet-->
<!--            v-if="to.state.selected instanceof WalletOptionModel"-->
<!--            :wallet="to.state.selected"-->
<!--          />-->
        </template>
        <template v-slot:action>
          <ExchangeAction
            :loading="createTransactionLoading || createQuoteLoading || isNewPayMethodLoading"
            @click.native="createTransaction"
          >
            {{ state.actionLabel }}
          </ExchangeAction>
        </template>
      </ExchangeFormLayout>
    </div>
    <div class="exchange-form__footer">
      <ApplePayButton
          v-if="canShowApplePayButton"
          :client-context-id-header="clientContextIdHeader"
          :amount="from.state.amount"
          :currency="from.state.selected.currency"
          :locale="i18n.locale"
          :invoice="null"
          :has-error="!!from.state.error"
          @payment-authorized="createTransaction"
          @error="createTransaction"
      ><a class="btn btn-xlg btn-primary">Apple Pay</a></ApplePayButton>
      <ExchangeFooterLoader v-if="createQuoteLoading || fetchRelationDetailsLoading || isNewPayMethodLoading" />
      <ZeroCommissionPromoDetails v-else-if="detailState.zeroCommission && props.isZeroCommissionAvailable" :title="detailState.zeroCommission" />
      <CommissionDetails v-else :commissions="commissionsToShow" />
    </div>
  </section>
</template>
<script lang="ts" setup>
import store from '@/store/assets';
import TokenService from '@paybis/frontend-common-lib/src/services/token.js';
import { getLocaleByLanguage } from '@paybis/frontend-common-lib/src/services/util.js';
import MoneyServiceTypes from '@paybis/frontend-common-lib/src/models/money-service/type.js';
import { getClientContextIdHeader, regenerateClientContextIdHeader } from '@paybis/frontend-common-lib/src/services/headers/clientContextId.js';
import { useMemoize } from '@vueuse/core';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { computed, onBeforeMount, reactive, ref, watch, watchEffect, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router/composables';
import Decimal from 'decimal.js';

import CommissionDetails from '@/components/ConvertDetails/CommissionDetails.vue';
import ZeroCommissionPromoDetails from '@/components/ConvertDetails/ZeroCommissionPromoDetails.vue';
import ExchangeAction from '@/components/ExchangeFormV2/ExchangeAction.vue';
import ExchangeDropDownCrypto from '@/components/ExchangeFormV2/ExchangeDropDownCrypto.vue';
import ExchangeDropDownCryptoWallets from '@/components/ExchangeFormV2/ExchangeDropDownCryptoWallets.vue';
import ExchangeDropDownFiat from '@/components/ExchangeFormV2/ExchangeDropDownFiat.vue';
import ExchangeFlowSelect from '@/components/ExchangeFormV2/ExchangeFlowSelect.vue';
import ExchangeFooterLoader from '@/components/ExchangeFormV2/ExchangeFooterLoader.vue';
import ExchangeFormItem from '@/components/ExchangeFormV2/ExchangeFormItem.vue';
import ExchangeFormItemError from '@/components/ExchangeFormV2/ExchangeFormItemError.vue';
import ExchangeFormLayout from '@/components/ExchangeFormV2/ExchangeFormLayout.vue';
import ExchangeFormPromoComponent from '@/components/ExchangeFormV2/ExchangeFormPromoComponent.vue';
import ExchangeFormPromoHint from '@/components/ExchangeFormV2/ExchangeFormPromoHint.vue';
import ExchangeFormSelectedWallet from '@/components/ExchangeFormV2/ExchangeFormSelectedWallet.vue';
import ExchangeInput from '@/components/ExchangeFormV2/ExchangeInput.vue';
import ExchangeInputEquivalent from '@/components/ExchangeFormV2/ExchangeInputEquivalent.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import ExchangeMoneyService from '@/components/ExchangeFormV2/ExchangeMoneyService.vue';
import ExchangeMoneyServiceLoading from '@/components/ExchangeFormV2/ExchangeMoneyServiceLoading.vue';
import PaymentSystemIcons from '@/components/ExchangeFormV2/PaymentSystemIcons.vue';
import PbPopup from '@/components/ExchangeFormV2/PbPopup.vue';
import {
  useCommission,
  useExchangeItem,
  usePromoCode,
  useSimpleAction,
  useSimpleItem,
  useSimpleList,
} from '@/composables';
import { formatNumberForCrypto } from '@/helpers/numberFormatter';
import { i18n } from '@/i18n/index.js';
import {
  BaseOptionModel,
  CryptoOptionModel,
  FiatOptionModel,
  PayMethodQuoteModel,
  SwapCryptoQuoteModel,
  WalletOptionModel,
} from '@/models';
import ExchangeFormService from '@/services/ExchangeFormService';
import PayMethodService from '@/services/PayMethodService';
import QuoteService, { QuoteValidationError } from '@/services/QuoteService';
import RateService from '@/services/RateService';
import TransactionService from '@/services/TransactionService';
import UserService from '@/services/UserService';
import WebWalletsService from '@/services/WebWalletsService';
import { ExchangeFormErrorCodes } from '@/constants/exchange-form';
import type {
  Currency,
  RelationLink,
  RequestBuyCryptoQuote,
  RequestCreateTransaction,
  RequestQuote,
  RequestSellCryptoQuote,
  RequestSwapCryptoQuote,
} from '@/types';
import { Direction, Flow, PaymentType, Promo } from '@/types';
import { ApplePayButton } from '@paybis/apple-pay-one-click';

interface Unit {
  id: string;
  currency: Currency;
  displayCurrency: string;
  icon: string;
  name: string;
  asset: Currency | null;
}

interface FlowCurrencyPair {
  defaultFiatCurrency: Currency;
  relationLink: RelationLink;
  from: Unit;
  to: Unit;
}

const clientContextIdHeader = ref(getClientContextIdHeader());

const props = withDefaults(defineProps<{
  from: Unit,
  to: Unit,
  fromAmount: string,
  toAmount: string,
  relationLink: RelationLink,
  feeCurrency: Currency,
  title: string,
  actionLabel: string,
  flow: Flow,
  defaultFlowCurrencyPairs: Record<Flow, FlowCurrencyPair>,
  availableBuyAssets: Currency[] | null,
  availableSellAssets: Currency[] | null,
  promo: PromoProp,
  lang: string,
  headingStyles: string,
  promoCode: string | null,
  isPromoCodeAvailable: boolean,
  isZeroCommissionAvailable: boolean,
  isUkUser: boolean,
}>(), {
  isPromoCodeAvailable: true,
  isZeroCommissionAvailable: true,
});
type PromoProp = Record<Promo, { isActive: boolean, title: string }>

const flow = ref<Flow>(props.flow);
const oldFlow = ref<Flow|null>(null);
const switchableFlows = [Flow.FIAT_TO_CRYPTO, Flow.CRYPTO_TO_FIAT];
const flowChangeHandler = (value: Flow) => {
  oldFlow.value = flow.value;
  flow.value = value;

  if (oldFlow.value !== value && switchableFlows.includes(oldFlow.value) && switchableFlows.includes(value)) {
    switchInputs();
  }
};

const onEventFlowSelected = (event: Event) => {
  const customEvent = event as CustomEvent<{ value: Flow }>;

  flowChangeHandler(customEvent.detail.value);
};
onMounted(() => window.addEventListener('nav-flow-selected', onEventFlowSelected));
onBeforeUnmount(() => window.removeEventListener('nav-flow-selected', onEventFlowSelected));

const getSwapRelations = useMemoize(() => ExchangeFormService.swapRelations());
const { state: availableSwapRelations, fetch: fetchSwapRelations } = useSimpleList({
  loading: false,
  list: () => getSwapRelations(),
});

const from = useExchangeItem(new BaseOptionModel(props.from), flow.value, props.fromAmount);
const to = useExchangeItem(new BaseOptionModel(props.to), flow.value, props.toAmount);

const fromFiatFlow = computed(() => [Flow.FIAT_TO_CRYPTO, Flow.FIAT_TO_FIAT].includes(flow.value));
const fromCryptoFlow = computed(() => [Flow.CRYPTO_TO_FIAT, Flow.CRYPTO_TO_CRYPTO].includes(flow.value));
const toFiatFlow = computed(() => [Flow.CRYPTO_TO_FIAT, Flow.FIAT_TO_FIAT].includes(flow.value));
const toCryptoFlow = computed(() => [Flow.FIAT_TO_CRYPTO, Flow.CRYPTO_TO_CRYPTO].includes(flow.value));

const relationLink = ref(props.relationLink);
const detailState = reactive({
  zeroCommission: props.promo[Promo.ZERO_COMMISSION]?.isActive ? props.promo[Promo.ZERO_COMMISSION].title : null,
});

const { state: promoCodeState } = usePromoCode(props.promoCode);

const state = reactive({
  headLabel: props.title,
  actionLabel: props.actionLabel,
});

function setDefaultFlowSelections(defaultFlow: FlowCurrencyPair) {
  from.state.selected = defaultFlow.from.asset !== null ? new CryptoOptionModel({
    ...defaultFlow.from,
    asset: defaultFlow.from.asset,
    defaultRelationLink: defaultFlow.relationLink,
    icon: defaultFlow.from.icon,
    defaultPayMethodCurrency: defaultFlow.defaultFiatCurrency,
  }) : new BaseOptionModel(defaultFlow.from);
  to.state.selected = defaultFlow.to.asset !== null ? new CryptoOptionModel({
    ...defaultFlow.to,
    asset: defaultFlow.to.asset,
    defaultRelationLink: defaultFlow.relationLink,
    icon: defaultFlow.to.icon,
    defaultPayMethodCurrency: defaultFlow.defaultFiatCurrency,
  }) : new BaseOptionModel(defaultFlow.to);
  relationLink.value = defaultFlow.relationLink;
  createQuote();
}

function setSelectedPaymentMethod() {
  const method = paymentMethodsState.items.find(item => item.id === from.state.selected.item.id) ?? paymentMethodsState.items[0];
  from.state.selected =  new BaseOptionModel({
    id: method.id,
    currency: method.currency,
    displayCurrency: method.currency,
    name: method.name,
  });
  relationLink.value = method.relationLink;
  createQuote();
}

function setSelectedPayoutMethod() {
  const method = payoutMethodsState.items.find(item => item.id === to.state.selected.item.id) ?? payoutMethodsState.items[0];
  to.state.selected =  new BaseOptionModel({
    id: method.id,
    currency: method.currency,
    displayCurrency: method.currency,
    name: method.name,
  });
  relationLink.value = method.relationLink;
  createQuote();
}

const switchFlowFromBuyCryptoToSwap = async (defaultFlow: FlowCurrencyPair) => {
  const toItem = to.state.selected.item;
  const relation = availableSwapRelations.items.find(({ to: swapTo }) => swapTo.slug === toItem.id || swapTo.assetId === toItem.currency);

  if (!relation) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  await fetchFromCryptoCurrencies();
  const selectedItem = fromCryptoCurrenciesState.items.find(item => item.id === relation.from.slug);
  if (!selectedItem) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  relationLink.value = relation.link;
  from.state.selected = selectedItem;
  createQuote();
};

const switchFlowFromSellCryptoToSwap = async (defaultFlow: FlowCurrencyPair) => {
  const fromItem = from.state.selected.item;
  const relation = availableSwapRelations.items.find(({ from: swapFrom }) => swapFrom.slug === fromItem.id || swapFrom.assetId === fromItem.currency);
  if (!relation) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  await fetchToCryptoCurrencies();
  const selectedItem = toCryptoCurrenciesState.items.find(item => item.id === relation.to.slug);
  if (!selectedItem) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  relationLink.value = relation.link;
  to.state.selected = selectedItem;
  createQuote();
};

const switchFlowFromSwapToSellCrypto = async (defaultFlow: FlowCurrencyPair) => {
  await fetchFromCryptoCurrencies();
  const fromItem = from.state.selected.item;
  const selectedItem = fromCryptoCurrenciesState.items.find(item => item.id === fromItem.id || item.currency === fromItem.currency);
  if (!selectedItem) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  to.state.selected = new BaseOptionModel(defaultFlow.to);
};

const switchFlowFromSwapToBuyCrypto = async (defaultFlow: FlowCurrencyPair) => {
  await fetchToCryptoCurrencies();
  const toItem = to.state.selected.item;
  const selectedItem = toCryptoCurrenciesState.items.find(item => item.id === toItem.id || item.currency === toItem.currency);
  if (!selectedItem) {
    setDefaultFlowSelections(defaultFlow);
    return;
  }
  from.state.selected = new BaseOptionModel(defaultFlow.from);
};

function useDropDown(currency: Currency) {
  const state = reactive({ currency });

  return {
    state,
  };
}

const { state: fromDropDownState } = useDropDown(from.state.selected.currency);
watch(() => from.state.showDropDown, (visible: boolean) => {
  if (!visible) {
    fromDropDownState.currency = from.state.selected.currency;
  }
});

const { state: toDropDownState } = useDropDown(to.state.selected.currency);
watch(() => to.state.showDropDown, (visible: boolean) => {
  if (!visible) {
    toDropDownState.currency = to.state.selected.currency;
  }
});

watch([() => from.state.selected.currency, () => to.state.selected.currency], () => {
  fromDropDownState.currency = from.state.selected.currency;
  toDropDownState.currency = to.state.selected.currency;
})

const switchInputs = () => {
  [from.state.selected, to.state.selected] = [to.state.selected, from.state.selected];
  [from.state.amount, to.state.amount] = [to.state.amount, from.state.amount];
  [from.state.equivalentAmount, to.state.equivalentAmount] = [to.state.equivalentAmount, from.state.equivalentAmount];

  const opposite = {
    [Changed.FROM]: Changed.TO,
    [Changed.FROM_EQUIVALENT]: Changed.TO_EQUIVALENT,
    [Changed.TO]: Changed.FROM,
    [Changed.TO_EQUIVALENT]: Changed.FROM_EQUIVALENT,
  };

  lastChanged.value = opposite[lastChanged.value];
};

const router = useRouter();

const relationDetails = useMemoize((relationLink: RelationLink) => ExchangeFormService.relationDetails(relationLink));
const {
  loading: fetchRelationDetailsLoading,
  action: fetchRelationDetails,
} = useSimpleAction(async (relationLink: RelationLink) => {
  const { title, buttonLabel, languages, promo } = await relationDetails(relationLink);
  state.headLabel = title;
  state.actionLabel = buttonLabel;
  detailState.zeroCommission = promo[Promo.ZERO_COMMISSION]?.isActive ? promo[Promo.ZERO_COMMISSION].title : null;

  // XXX: Skip, if fiat payment method doesn't support selected language
  if (!ExchangeFormService.checkIfExistLanguage({
    languages,
    relationLink,
    currentLanguage: props.lang,
  })) return;

  router.replace({ name: 'Home', params: { buyLink: relationLink } }).catch(() => {
  });
});

const { action: updatePageContent } = useSimpleAction(
  ({ relationLink, fromCrypto, toCrypto, currencyCodeFrom, currencyCodeTo }: {
    relationLink: RelationLink,
    fromCrypto: boolean,
    toCrypto: boolean,
    currencyCodeFrom: string,
    currencyCodeTo: string,
  }) => ExchangeFormService.updatePageContent({
    relationLink,
    cryptoAssetId: '', // TODO: need to send asset ID here
    isServiceFromCrypto: fromCrypto,
    isServiceToCrypto: toCrypto,
    currencyCodeFrom,
    currencyCodeTo,
  }),
);

const { loading: createTransactionLoading, action: createTransaction } = useSimpleAction(
  () => {
    clientContextIdHeader.value = regenerateClientContextIdHeader();
    if (!requestedAmount.value) {
      from.state.error = i18n.t('exchange-form.error.no-amount').toString();
    }

    if (from.state.error || to.state.error) {
      return Promise.reject();
    }

    const payload = {
      quoteId: quoteId.value,
    } as RequestCreateTransaction;

    const selectedPaybisWalletAddress = from.state.selected.primaryAddress;
    if (selectedPaybisWalletAddress) {
      payload.webwalletAddress = selectedPaybisWalletAddress;
    }

    const { maxSendBalance, minimumAllowedBalance } = from.state.selected;
    if (maxSendBalance && maxSendBalance.lessThan(new Decimal(from.state.amount)) && minimumAllowedBalance > 0) {
      from.state.error = i18n.t('exchange-form.error.over-min-balance', {
        '{limit}': minimumAllowedBalance,
        '{currency}': from.state.selected.currency,
      }).toString();
      return;
    }

    return TransactionService.createTransaction(payload)
      .then(window.dataLayer.push({
        event: 'begin_checkout',
        amountBuy: to.state.amount,
        amountSell: from.state.amount,
        currencyBuy: to.state.selected.currency,
        currencySell: from.state.selected.currency,
        ecommerce: {
          items: [{
            item_name: to.state.selected.currency,
            item_id: flow.value === 'crypto-to-fiat' ? from.state.selected.currency : to.state.selected.currency,
            quantity: 1,
          }],
        },
      }));
  },
  (error: unknown) => {
    if (axios.isAxiosError(error) || error?.status === 400) {
      from.state.error = error.data?.errors[0]?.message ?? null;
    }

    clientContextIdHeader.value = regenerateClientContextIdHeader();
  },
);

// From FIAT
const fromFiatCurrencies = useMemoize((flow: Flow) => ExchangeFormService.fromFiatCurrencies(flow));
const { state: fromFiatCurrenciesState, fetch: fetchFromFiatCurrencies } = useSimpleList({
  list: () => fromFiatCurrencies(flow.value),
});

// From CRYPTO
const fromCryptoCurrencies = useMemoize((flow: Flow) => ExchangeFormService.fromCryptoCurrencies(flow));
const { state: fromCryptoCurrenciesState, fetch: fetchFromCryptoCurrencies } = useSimpleList({
  list: () => fromCryptoCurrencies(flow.value),
});

const fromCryptoCurrenciesList = computed(() => {
  const list = fromCryptoCurrenciesState.items;
  const availableSellAssets = props.availableSellAssets;
  if (availableSellAssets) {
    return list.filter(item => availableSellAssets.includes(item.currency));
  }
  return list;
});

// To FIAT
const toFiatCurrencies = useMemoize((flow: Flow) => ExchangeFormService.toFiatCurrencies(flow));
const { state: toFiatCurrenciesState, fetch: fetchToFiatCurrencies } = useSimpleList({
  list: () => toFiatCurrencies(flow.value),
});

// To CRYPTO
const toCryptoCurrencies = useMemoize((flow: Flow) => ExchangeFormService.toCryptoCurrencies(flow));
const { state: toCryptoCurrenciesState, fetch: fetchToCryptoCurrencies } = useSimpleList({
  list: () => toCryptoCurrencies(flow.value),
});
const toCryptoCurrenciesList = computed(() => {
  const list = toCryptoCurrenciesState.items;
  const availableBuyAssets = props.availableBuyAssets;
  if (availableBuyAssets) {
    return list.filter(item => availableBuyAssets.includes(item.currency));
  }
  return list;
});

const user = UserService.getUser;

const { state: userState, fetch: fetchUser } = useSimpleItem({
  get: () => user(),
  fulfilled: [
    async (user) => {
      if (user.allowWebWallets) {
        await Promise.all([
          fetchWebWallets(),
          fetchSupportedAssets(),
          fetchCryptoRate(),
        ]);
        const fromWallet = webWalletsOptions.value
          .find((wallet) => wallet.id === from.state.selected.currency && !wallet.isEmpty);

        if (fromWallet) {
          from.state.selected = fromWallet;
        }

        // const toWallet = webWalletsOptions.value
        //   .find((wallet) => wallet.id === to.state.selected.currency);
        //
        // if (toWallet) {
        //   to.state.selected = toWallet;
        // }
      }
    },
  ],
  loading: false,
},
);

onBeforeMount(() => {
  if (TokenService.hasToken()) {
    fetchUser();
  }
});
const allowWebWallets = computed(() => userState.item?.allowWebWallets ?? true);

// WALLETS
const webWallets = useMemoize(() => WebWalletsService.listWallets());
const { state: webWalletsState, fetch: fetchWebWallets } = useSimpleList({
  loading: false,
  list: () => webWallets(),
});
const supportedAssets = useMemoize(() => WebWalletsService.listAssets());
const { state: supportedAssetsState, fetch: fetchSupportedAssets } = useSimpleList({
  loading: false,
  list: () => supportedAssets(),
});
const cryptoRates = useMemoize((currency?: Currency) => RateService.crypto(currency));
const { state: cryptoRateState, fetch: fetchCryptoRate } = useSimpleList({
  loading: false,
  list: () => cryptoRates(),
});

const treatAsGrossAmount = ref<boolean>(false);
watch([() => from.state.amount, () => to.state.amount, () => from.state.selected], () => {
  treatAsGrossAmount.value = false;

  const { amount, selected } = from.state;
  if (!(selected instanceof WalletOptionModel) || !amount) {
    return;
  }

  treatAsGrossAmount.value = selected.maxSendBalance.equals(new Decimal(amount));
});

watch([fromFiatFlow, toFiatFlow, () => from.state.selected.currency, () => to.state.selected.currency], fetchCryptoRate);

const webWalletsOptions = computed(() => {
  return WebWalletsService.walletWithBalanceOptionsModelFactory(
    webWalletsState.items,
    supportedAssetsState.items,
    cryptoRateState.items,
    i18n.t('exchange-form.paybis-wallet.option-name').toString(),
  ) ?? [];
});

const fromWebWalletOptions = computed(() => webWalletsOptions.value.filter((wallet: WalletOptionModel) =>
    wallet.hasPositiveBalance && fromCryptoCurrenciesState.items.find((crypto: CryptoOptionModel) => wallet.id === crypto.currency))
    .sort((a, b) => parseFloat(b.fiat.amount) - parseFloat(a.fiat.amount)),
);

// const toWebWalletOptions = computed(() => webWalletsOptions.value.filter((wallet: WalletOptionModel) =>
//     toCryptoCurrenciesList.value.find((crypto) => wallet.id === crypto.currency)
// ));

const webWalletsOptionsLoading = computed(() => {
  return webWalletsState.loading || supportedAssetsState.loading || cryptoRateState.loading || userState.loading;
});

watch(flow, async (value) => {
  const defaultFlow = props.defaultFlowCurrencyPairs[value];
  if (value === Flow.CRYPTO_TO_CRYPTO) {
    await fetchSwapRelations();
    if (oldFlow.value === Flow.FIAT_TO_CRYPTO) {
      await switchFlowFromBuyCryptoToSwap(defaultFlow);
    }
    if (oldFlow.value === Flow.CRYPTO_TO_FIAT) {
      await switchFlowFromSellCryptoToSwap(defaultFlow);
    }
  }

  if (oldFlow.value === Flow.CRYPTO_TO_CRYPTO) {
    if (value === Flow.CRYPTO_TO_FIAT) {
      await switchFlowFromSwapToSellCrypto(defaultFlow);
    }
    if (value === Flow.FIAT_TO_CRYPTO) {
      await switchFlowFromSwapToBuyCrypto(defaultFlow);
    }
  }

  if (from.state.selected instanceof CryptoOptionModel) {
    const fromWallet = webWalletsOptions.value
      .find((wallet) => wallet.id === from.state.selected.currency && !wallet.isEmpty);

    if (fromWallet) {
      from.state.selected = fromWallet;
    }
  } else if ((from.state.selected instanceof WalletOptionModel) && from.state.selected.isEmpty) {
    await fetchFromCryptoCurrencies();
    const selectedCrypto = fromCryptoCurrenciesState.items
      .find((crypto) => crypto.currency === from.state.selected.id);

    from.state.selected = selectedCrypto ? selectedCrypto : fromCryptoCurrenciesState.items[0];
  }

  // if (to.state.selected instanceof CryptoOptionModel) {
  //   const toWallet = webWalletsOptions.value
  //     .find((wallet) => wallet.id === to.state.selected.currency);
  //
  //   if (toWallet) {
  //     to.state.selected = toWallet;
  //   }
  // } else
  if (to.state.selected instanceof WalletOptionModel) {
    await fetchToCryptoCurrencies();

    const selectedCrypto = toCryptoCurrenciesList.value
      .find((crypto: CryptoOptionModel) => crypto.currency === to.state.selected.id);

    // if (!selectedCrypto) {
    //   to.state.selected = toCryptoCurrenciesList.value[0];
    // }
    to.state.selected = selectedCrypto ?? toCryptoCurrenciesList.value[0];
  }
});

const enum Changed {
  FROM = 'from',
  FROM_EQUIVALENT = 'from-equivalent',
  TO = 'to',
  TO_EQUIVALENT = 'to-equivalent',
}

const lastChanged = ref<Changed>((!props.fromAmount && props.toAmount > 0) ? Changed.TO : Changed.FROM);

const requestedAmount = computed(() => {
  switch (lastChanged.value) {
    case Changed.FROM:
    default:
      return from.state.amount;
    case Changed.FROM_EQUIVALENT:
      return from.state.equivalentAmount;
    case Changed.TO:
      return to.state.amount;
    case Changed.TO_EQUIVALENT:
      return to.state.equivalentAmount;
  }
});
const currencyCode = computed(() => {
  switch (lastChanged.value) {
    case Changed.FROM:
    case Changed.TO_EQUIVALENT:
      return from.state.selected.currency;
  }

  return to.state.selected.currency;
});
const buyCurrencyCode = computed(() => {
  switch (lastChanged.value) {
    case Changed.FROM:
    case Changed.TO_EQUIVALENT:
      return fromDropDownState.currency;
  }

  return fromDropDownState.currency;
});
const sellCurrencyCode = computed(() => {
  switch (lastChanged.value) {
    case Changed.FROM:
    case Changed.TO_EQUIVALENT:
      return toDropDownState.currency;
  }

  return toDropDownState.currency;
});
const requestedAmountType = computed(() => {
  switch (lastChanged.value) {
    case Changed.FROM:
    case Changed.FROM_EQUIVALENT:
      return Direction.FROM;
  }

  return Direction.TO;
});

const listFiatPaymentMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPaymentMethods(from, to),
);

const { state: fromDropDownMethods, fetch: fromDropDownMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPaymentMethods(fromDropDownState.currency, toDropDownState.currency),
});

const { state: fromDropDownQuoteList, fetch: listBuyCryptoQuoteFetch } = useSimpleList({
  loading: false,
  list: () => {
    from.state.error = null;
    to.state.error = null;

    const payload = {
      currencyCodeFrom: fromDropDownState.currency,
      currencyCodeTo: toDropDownState.currency,
      requestedAmount: {
        amount: requestedAmount.value,
        currencyCode: currencyCode.value,
      },
      requestedAmountType: requestedAmountType.value,
      promoCode: promoCodeState.data.code || null,
    } as RequestBuyCryptoQuote;

    try {
      return QuoteService.listBuyCryptoQuote(payload).then(data => data.paymentMethods);
    } catch (e) {
      if (e instanceof QuoteValidationError) {
        from.state.error = e.message;
      }
      throw e;
    }
  },
});
watch([fromDropDownState, () => from.state.showDropDown], async () => {
  if (!from.state.showDropDown) {
    return;
  }
  switch (flow.value) {
    case Flow.FIAT_TO_CRYPTO:
      fromDropDownMethodsFetch();
      fetchFromFiatCurrencies();

      if (parseFloat(requestedAmount.value) > 0) {
        listBuyCryptoQuoteFetch();
      }
      break;
    case Flow.CRYPTO_TO_FIAT:
    case Flow.CRYPTO_TO_CRYPTO:
      fetchFromCryptoCurrencies();
      break;
  }
});

const fromFiatMethodsLoading = computed(() => fromDropDownMethods.loading || fromDropDownQuoteList.loading);

const fromDropDownList = computed(() => {
  return fromDropDownMethods.items.map(item => {
    const quote = fromDropDownQuoteList.items.find(quote => quote.id === item.id) ?? null;
    return new FiatOptionModel(item, quote);
  });
});

const { state: paymentMethodsState, fetch: paymentMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPaymentMethods(from.state.selected.currency, to.state.selected.currency),
});
watch([() => from.state.selected.currency, () => to.state.selected.currency], () => {
  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    paymentMethodsFetch();
  }
});

watch(() => paymentMethodsState.items, () => {
  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    if (paymentMethodsState.items.length === 0) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[Flow.FIAT_TO_CRYPTO]);
    } else {
      setSelectedPaymentMethod();
    }
  }
});

const listFiatPayoutMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPayoutMethods(from, to),
);

const { state: toDropDownMethods, fetch: toDropDownMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPayoutMethods(fromDropDownState.currency, toDropDownState.currency),
});

const { state: toDropDownQuoteList, fetch: toDropDownQuoteListFetch } = useSimpleList({
  loading: false,
  list: () => {
    from.state.error = null;
    to.state.error = null;

    const payload = {
      currencyCodeFrom: fromDropDownState.currency,
      currencyCodeTo: toDropDownState.currency,
      requestedAmount: {
        amount: requestedAmount.value,
        currencyCode: currencyCode.value,
      },
      requestedAmountType: requestedAmountType.value,
      promoCode: promoCodeState.data.code || null,
    } as RequestSellCryptoQuote;

    if (from.state.selected instanceof WalletOptionModel) {
      payload.paymentType = PaymentType.WALLET;
      payload.address = from.state.selected.primaryAddress;
      payload.sendAll = treatAsGrossAmount.value;
    }

    try {
      return QuoteService.listSellCryptoQuote(payload).then(data => data.payoutMethods);
    } catch (e) {
      if (e instanceof QuoteValidationError) {
        from.state.error = e.message;
      }
      throw e;
    }
  },
});
watch([toDropDownState, () => to.state.showDropDown], () => {
  if (!to.state.showDropDown) {
    return;
  }
  switch (flow.value) {
    case Flow.CRYPTO_TO_FIAT:
      toDropDownMethodsFetch();
      fetchToFiatCurrencies();

      if (parseFloat(requestedAmount.value) > 0) {
        toDropDownQuoteListFetch();
      }
      break;
    case Flow.FIAT_TO_CRYPTO:
    case Flow.CRYPTO_TO_CRYPTO:
      fetchToCryptoCurrencies();
      break;
  }
});

const toDropDownList = computed(() => {
  return toDropDownMethods.items.map(item => {
    const quote = toDropDownQuoteList.items.find(quote => quote.id === item.id) ?? null;
    return new FiatOptionModel(item, quote);
  });
});

const { state: payoutMethodsState, fetch: payoutMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPayoutMethods(from.state.selected.currency, to.state.selected.currency),
});
watch([() => from.state.selected.currency, () => to.state.selected.currency], () => {
  if (flow.value === Flow.CRYPTO_TO_FIAT) {
    payoutMethodsFetch();
    fetchToFiatCurrencies();
  }
});
const toFiatMethodsLoading = computed(() => toDropDownMethods.loading || toDropDownQuoteList.loading || toFiatCurrenciesState.loading);

watch(flow, () => {
  switch (flow.value) {
    case Flow.FIAT_TO_CRYPTO:
      payoutMethodsState.items = [];
      break;
    case Flow.CRYPTO_TO_FIAT:
      paymentMethodsState.items = [];
      break;
    case Flow.CRYPTO_TO_CRYPTO:
      paymentMethodsState.items = [];
      payoutMethodsState.items = [];
      break;
  }
});
watch(() => payoutMethodsState.items, () => {
  if (flow.value === Flow.CRYPTO_TO_FIAT) {
    if (payoutMethodsState.items.length === 0) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[Flow.CRYPTO_TO_FIAT]);
    } else {
      setSelectedPayoutMethod();
    }
  }
});

const fromSelected = computed({
  get: () => from.state.selected,
  set: async (option: BaseOptionModel) => {
    from.state.selected = option;
    if (option instanceof FiatOptionModel) {
      relationLink.value = option.relationLink;
    }

    if (flow.value === Flow.FIAT_TO_CRYPTO && from.state.amount) {
      createQuote();
      return;
    }

    if (flow.value !== Flow.CRYPTO_TO_CRYPTO) {
      return;
    }
    // For swap crypto we need to make sure that we have an available relation for given selection
    await fetchSwapRelations();

    const fromSelectedAssetId = from.state.selected.assetId;
    const toSelectedAssetId = to.state.selected.assetId;

    const selectedRelation = availableSwapRelations.items.find(({ from: fromService, to: toService }) => fromService.assetId === fromSelectedAssetId && toService.assetId === toSelectedAssetId);
    if (selectedRelation) {
      relationLink.value = selectedRelation.link;
      createQuote();
      return;
    }
    const fromRelation = availableSwapRelations.items.find(({ from: fromService }) => fromService.assetId === fromSelectedAssetId);
    if (!fromRelation) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[flow.value]);
      return;
    }
    await fetchToCryptoCurrencies();
    to.state.selected = toCryptoCurrenciesState.items.find(item => item.assetId === fromRelation.to.assetId) ?? toCryptoCurrenciesState.items[0];
    relationLink.value = fromRelation.link;
    createQuote();
  },
});

const toSelected = computed({
  get: () => to.state.selected,
  set: async (option: BaseOptionModel) => {
    to.state.selected = option;
    if (option instanceof FiatOptionModel) {
      relationLink.value = option.relationLink;
    }

    if (flow.value === Flow.CRYPTO_TO_FIAT && to.state.amount) {
      createQuote();
      return;
    }

    if (flow.value !== Flow.CRYPTO_TO_CRYPTO) {
      return;
    }
    // For swap crypto we need to make sure that we have an available relation for given selection
    await fetchSwapRelations();

    const fromSelectedAssetId = from.state.selected.assetId;
    const toSelectedAssetId = to.state.selected.assetId;

    const selectedRelation = availableSwapRelations.items.find(({ from: fromService, to: toService }) => fromService.assetId === fromSelectedAssetId && toService.assetId === toSelectedAssetId);
    if (selectedRelation) {
      relationLink.value = selectedRelation.link;
      createQuote();
      return;
    }
    const toRelation = availableSwapRelations.items.find(({ to: toService }) => toService.assetId === toSelectedAssetId);
    if (!toRelation) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[flow.value]);
      return;
    }
    await fetchFromCryptoCurrencies();
    from.state.selected = fromCryptoCurrenciesState.items.find(item => item.assetId === toRelation.from.assetId) ?? fromCryptoCurrenciesState.items[0];
    relationLink.value = toRelation.link;
    createQuote();
  },
});

interface SelectedMethodItem {
  currency: string,
  name: string,
}

function getSelectedMethod(selectedItem: BaseOptionModel, allowedFlows: Flow[]): SelectedMethodItem {
  const defaultSelection: SelectedMethodItem = {
    currency: selectedItem.displayCurrency,
    name: selectedItem.name,
  };

  if (!allowedFlows.includes(flow.value)) {
    return defaultSelection;
  }

  const assetInfo = store.getters['assets/getAssetById'](selectedItem.currency);

  if (!assetInfo) {
    return defaultSelection;
  }

  return {
    currency: assetInfo.currency.code,
    name: assetInfo.blockchain.name,
  };
}

const fromSelectedMethod = computed<SelectedMethodItem>(() => getSelectedMethod(from.state.selected, [Flow.CRYPTO_TO_FIAT, Flow.CRYPTO_TO_CRYPTO]));
const toSelectedMethod = computed<SelectedMethodItem>(() => getSelectedMethod(to.state.selected, [Flow.FIAT_TO_CRYPTO, Flow.CRYPTO_TO_CRYPTO]));

const quote = ref<PayMethodQuoteModel | SwapCryptoQuoteModel | null>(null);

watch([
  relationLink,
  fromCryptoFlow,
  toCryptoFlow,
  () => from.state.selected.currency,
  () => to.state.selected.currency,
], () => {
  fetchRelationDetails(relationLink.value);
  updatePageContent({
    relationLink: relationLink.value,
    fromCrypto: fromCryptoFlow.value,
    toCrypto: toCryptoFlow.value,
    currencyCodeFrom: from.state.selected.currency,
    currencyCodeTo: to.state.selected.currency,
  });
});
watch(relationLink, () => {
  fetchRelationDetails(relationLink.value);
});

watch(() => to.state.selected.currency, (value) => {
  commissions.service.currency = to.state.selected.displayCurrency ?? value;
  commissions.network.currency = to.state.selected.displayCurrency ?? value;
});

const { commissions, reset: resetCommissions } = useCommission(
  {
    name: i18n.t('exchange-form.service-fee').toString(),
    amount: '0.00',
    currency: store.getters['assets/getAssetById'](props.feeCurrency)?.currency.code ?? props.feeCurrency,
  },
  {
    name: i18n.t('exchange-form.network-fee').toString(),
    amount: '0.00',
    currency: store.getters['assets/getAssetById'](props.feeCurrency)?.currency.code ?? props.feeCurrency,
  },
  {
    name: 'Blockchain fee',
    amount: '',
    currency: '' as Currency,
  },
);

const commissionsToShow = computed(() => {
  if (!quote.value) {
    return [commissions.service, commissions.network];
  }

  const feesInCrypto = quote.value.feesInCrypto;
  const fees = feesInCrypto ? quote.value.feesInCrypto : quote.value.fees;

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  commissions.service.currency = fees.serviceFee.currencyCode;
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  commissions.network.currency = fees.networkFee.currencyCode;
  if (feesInCrypto) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.service.amount = getFormattedCryptoAmount(fees.serviceFee.amount, commissions.service.currency);
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.network.amount = getFormattedCryptoAmount(fees.networkFee.amount, commissions.network.currency);
  } else {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.service.amount = fees.serviceFee.amount;
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.network.amount = fees.networkFee.amount;
  }

  if (fees.blockchainFee) {
    if (fees.blockchainFee.currencyCode === commissions.network.currency) {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      commissions.network.amount = new Decimal(commissions.network.amount).plus(fees.blockchainFee.amount).toString();

      if (feesInCrypto) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        commissions.network.amount = getFormattedCryptoAmount(commissions.network.amount, commissions.network.currency);
      }

      return [commissions.service, commissions.network];
    }

    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.blockchain.currency = fees.blockchainFee.currencyCode;
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.blockchain.amount = getFormattedCryptoAmount(fees.blockchainFee.amount, commissions.blockchain.currency);

    return [
      commissions.service,
      [commissions.network, commissions.blockchain],
    ];
  }

  return [commissions.service, commissions.network];
});

const getFormattedCryptoAmount = (cryptoAmount: string, cryptoCurrency: string) => {
  return formatNumberForCrypto(cryptoAmount, i18n.locale, cryptoCurrency);
};

watchEffect(() => {
  if (quote.value) {
    from.state.error = quote.value.error;
  }
});

watch(quote, () => {
  if (quote.value) {
    if (lastChanged.value !== Changed.FROM) {
      from.state.amount = quote.value.amountFrom.amount;
    }
    if (lastChanged.value !== Changed.FROM_EQUIVALENT) {
      from.state.equivalentAmount = quote.value.amountFromEquivalent?.amount ?? '';
    }
    if (lastChanged.value !== Changed.TO) {
      to.state.amount = quote.value.amountTo.amount;
    }
    if (lastChanged.value !== Changed.TO_EQUIVALENT) {
      to.state.equivalentAmount = quote.value.amountToEquivalent?.amount ?? '';
    }

    const isFeesInCrypto = quote.value.feesInCrypto && flow.value === Flow.CRYPTO_TO_CRYPTO;
    const fees = isFeesInCrypto ? quote.value.feesInCrypto : quote.value.fees;

    if (!promoCodeState.data.error && fees.feeDiscount) {
      const discountAmount = Number.parseFloat(fees.feeDiscount.amount);
      const discountCurrency = fees.feeDiscount.currencyCode;

      const amount = isFeesInCrypto
        ? `${discountCurrency} ${discountAmount.toFixed(8)}`
        : discountAmount.toLocaleString(getLocaleByLanguage(i18n.locale), {
          style: 'currency',
          currency: discountCurrency,
        });
      promoCodeState.data.capture = i18n.t('exchange-form.promo.discount', { amount }).toString();
    }
    if (promoCodeState.data.code && !promoCodeState.data.error && !fees.feeDiscount) {
      promoCodeState.data.error = false;
      promoCodeState.data.capture = i18n.t('exchange-form.promo.already-no-paybis-fee').toString();
    }
  }
});

const minMaxAmountError = computed(() => {
  if (!from.state.error) {
    return null;
  }

  const match = from.state.error.match(/(\d+(\.\d+)?)/);
  if (!match) {
    return null;
  }

  const amount = match[0];
  const [prefix, suffix] = from.state.error.split(amount);

  return { prefix, amount, suffix };
});

const handleMinMaxAmountClick = () => {
  const isChangedFrom = ([Changed.FROM, Changed.TO_EQUIVALENT].includes(lastChanged.value));
  const state = isChangedFrom ? from.state : to.state;
  const amountHandler = isChangedFrom ? fromAmountHandler : toAmountHandler;
  const { amount, suffix } = minMaxAmountError.value;

  let selectedCurrency = state.selected.currency;
  const assetInfo = store.getters['assets/getAssetById'](state.selected.currency)
  if (assetInfo) {
    selectedCurrency = assetInfo.currency.code;
  }

  // Case, if error is from createTransaction(), it always returns fiat amount for "from" side
  if (Changed.FROM === lastChanged.value && !suffix.includes(selectedCurrency)) {
    lastChanged.value = Changed.TO_EQUIVALENT;
    state.equivalentAmount = amount;
    fromAmountEquivalentHandler(amount);
    return
  }

  state.amount = amount;
  amountHandler(amount, lastChanged.value);
};

const quoteUpdateTimeout = 60000;
const quoteTimeoutId = ref(-1);
const quoteId = ref(null);
const { loading: createQuoteLoading, action: createQuote } = useSimpleAction(async () => {
  if (!requestedAmount.value || !parseFloat(requestedAmount.value) || parseFloat(requestedAmount.value) <= 0) {
    return;
  }

  clearTimeout(quoteTimeoutId.value);

  from.state.error = null;
  to.state.error = null;

  const params = {
    currencyCodeFrom: from.state.selected.currency,
    currencyCodeTo: to.state.selected.currency,
    requestedAmount: {
      amount: requestedAmount.value,
      currencyCode: currencyCode.value,
    },
    requestedAmountType: requestedAmountType.value,
    promoCode: promoCodeState.data.code || null,
  } as RequestQuote;

  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    let response = null;

    try {
      response = await QuoteService.listBuyCryptoQuote({ ...params, paymentMethod: from.state.selected.id });
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }
    quote.value = response.paymentMethods[0];
    quoteId.value = response.id;
    if (response.promoCode?.error) {
      promoCodeState.data.error = true;
      promoCodeState.data.capture = response.promoCode.error.message;
    } else {
      promoCodeState.data.error = false;
    }
  } else if (flow.value === Flow.CRYPTO_TO_FIAT) {
    const payload = {
      ...params,
      payoutMethod: to.state.selected.id,
    } as RequestSellCryptoQuote;
    if (from.state.selected instanceof WalletOptionModel) {
      payload.paymentType = PaymentType.WALLET;
      payload.address = from.state.selected.primaryAddress;
      payload.sendAll = treatAsGrossAmount.value;
    }
    let response;
    try {
      response = await QuoteService.listSellCryptoQuote(payload);
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }
    quote.value = response.payoutMethods[0];
    quoteId.value = response.id;

    if (response.promoCode?.error) {
      promoCodeState.data.error = true;
      promoCodeState.data.capture = response.promoCode.error.message;
    } else {
      promoCodeState.data.error = false;
    }
  } else if (flow.value === Flow.CRYPTO_TO_CRYPTO) {
    const payload = { ...params } as RequestSwapCryptoQuote;
    if (from.state.selected instanceof WalletOptionModel) {
      payload.paymentType = PaymentType.WALLET;
      payload.address = from.state.selected.primaryAddress;
      payload.sendAll = treatAsGrossAmount.value;
    }
    if (to.state.selected instanceof WalletOptionModel) {
      payload.payoutType = PaymentType.WALLET;
      payload.address = to.state.selected.primaryAddress;
    }
    let swapQuote;
    try {
      swapQuote = await QuoteService.listSwapCryptoQuote(payload);
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }
    quote.value = swapQuote;
    relationLink.value = swapQuote.relationLink;
    quoteId.value = swapQuote.id;

    if (swapQuote?.item?.errors) {
      promoCodeState.data.error = true;
      promoCodeState.data.capture = swapQuote.item.errors[0].message;
    } else {
      promoCodeState.data.error = false;
    }
  }
});

watch(quote, () => {
  clearTimeout(quoteTimeoutId.value);
  if (quote.value) {
    quoteTimeoutId.value = window.setTimeout(createQuote, quoteUpdateTimeout);
  }
});

onBeforeMount(() => createQuote());

const requestQuote = debounce(createQuote, 500);

watch(() => promoCodeState.data.code, () => requestQuote());

const clearAmounts = (type: Changed) => {
  if (type !== Changed.FROM) {
    from.state.amount = '';
  }
  if (type !== Changed.FROM_EQUIVALENT) {
    from.state.equivalentAmount = '';
  }
  if (type !== Changed.TO) {
    to.state.amount = '';
  }
  if (type !== Changed.TO_EQUIVALENT) {
    to.state.equivalentAmount = '';
  }

  resetCommissions();
  if (quote.value) {
    quote.value = null;
  }
};

const isNewPayMethodLoading = computed(() => {
  return (!from.state.showDropDown && fromDropDownMethods.loading) || (!to.state.showDropDown && payoutMethodsState.loading);
});

const fromLoading = computed(() => {
  if ([Changed.FROM, Changed.FROM_EQUIVALENT].includes(lastChanged.value)) {
    return false;
  }

  return createQuoteLoading.value || payoutMethodsState.loading || availableSwapRelations.loading;
});

const toLoading = computed(() => {
  if ([Changed.TO, Changed.TO_EQUIVALENT].includes(lastChanged.value)) {
    return false;
  }

  return createQuoteLoading.value || paymentMethodsState.loading || availableSwapRelations.loading;
});

const amountHandler = (value: string, type: Changed) => {
  lastChanged.value = type;

  if (value.length === 0 || Number.parseFloat(value) === 0) {
    clearAmounts(type);
    return;
  }

  requestQuote();
};

const fromAmountHandler = (value: string) => amountHandler(value, Changed.FROM);
const fromAmountEquivalentHandler = (value: string) => amountHandler(value, Changed.FROM_EQUIVALENT);
const toAmountHandler = (value: string) => amountHandler(value, Changed.TO);
const toAmountEquivalentHandler = (value: string) => amountHandler(value, Changed.TO_EQUIVALENT);

document.body.addEventListener('exchange-form__from-amount', ({ detail }: { detail: string }) => {
  from.state.amount = detail;
  fromAmountHandler(detail);
});

function onClickCryptoBalance(value: string) {
  from.state.amount = value;
  fromAmountHandler(value);
}

const canShowApplePayButton = computed(() => !props.isUkUser && from.state?.selected?.item?.id === MoneyServiceTypes.TYPE_BANK_CARD);
</script>
